import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, LogIn, Lock } from "lucide-react";
import { toast } from "sonner";

const ADMIN_ROLES = new Set(["admin", "moderator", "super_admin"]);

/**
 * Clean admin login page. Submits to /api/admin/auth/login accepting either
 * `username` or `email` (auto-detected by presence of `@`). On success the
 * server returns { access_token, user }. We stash the token so subsequent
 * API calls can send it as a Bearer header, refresh AuthContext, and
 * redirect to /admin/dashboard.
 */
export default function AdminLogin() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/admin/dashboard";

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && user && ADMIN_ROLES.has(user.role)) {
      nav(from, { replace: true });
    }
  }, [loading, user, nav, from]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const trimmed = identifier.trim();
    const payload = trimmed.includes("@")
      ? { email: trimmed, password }
      : { username: trimmed, password };
    try {
      const { data } = await api.post("/admin/auth/login", payload);
      if (data?.access_token) {
        try { localStorage.setItem("admin_access_token", data.access_token); } catch {}
      }
      await refresh();
      toast.success("Signed in");
      nav(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Invalid credentials";
      setError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-6 h-6 text-amber-400" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-400">Relaxora</p>
            <h1 className="text-lg font-medium text-stone-100">Moderation panel</h1>
          </div>
        </div>

        <form onSubmit={submit} className="bg-stone-950 border border-stone-800 rounded-2xl p-6 space-y-4 shadow-2xl">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-stone-400">Username or email</label>
            <input
              type="text" required autoComplete="off" autoCapitalize="none"
              value={identifier} onChange={(e) => setIdentifier(e.target.value)}
              className="mt-1.5 w-full h-11 px-3 rounded-lg bg-stone-900/60 border border-stone-700 text-stone-100 text-sm focus:border-amber-500/60 outline-none"
              data-testid="adm-login-username"
              placeholder="Username or email"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-stone-400">Password</label>
            <input
              type="password" required autoComplete="current-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full h-11 px-3 rounded-lg bg-stone-900/60 border border-stone-700 text-stone-100 text-sm focus:border-amber-500/60 outline-none"
              data-testid="adm-login-password"
            />
          </div>
          {error && (
            <p className="text-xs text-red-400" data-testid="adm-login-error">{error}</p>
          )}
          <button
            type="submit" disabled={busy}
            className="w-full h-11 rounded-lg bg-amber-500 text-stone-950 font-semibold flex items-center justify-center gap-2 hover:bg-amber-400 disabled:opacity-50"
            data-testid="adm-login-submit"
          ><LogIn className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}</button>
          <p className="pt-2 text-[11px] text-stone-500 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Restricted area — admin, moderator &amp; super-admin only.
          </p>
        </form>
      </div>
    </div>
  );
}
